import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AuthShell, AuthError, AuthSuccess } from '../components/auth/AuthShell';
import { useAuthStore } from '../store/useAuthStore';
import { useI18n } from '../contexts/I18nContext';
import * as authApi from '../lib/authApi';

type Status = 'pending' | 'success' | 'failed';

export function VerifyEmailPage() {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const { t } = useI18n();

  const [status, setStatus] = useState<Status>('pending');
  const [resendEmail, setResendEmail] = useState('');
  const [resent, setResent] = useState(false);
  const [resending, setResending] = useState(false);
  // Guard against React StrictMode double-mount: the token is single-use, so
  // firing the POST twice would make the second call fail spuriously.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    let cancelled = false;
    authApi
      .verifyEmail(token)
      .then((session) => {
        if (cancelled) return;
        setStatus('success');
        // The emailed token proves mailbox ownership → auto-login, landing on
        // the daily summary like every other authenticated entry (CLE-448).
        setSession(session);
        navigate(authApi.postAuthLandingPath(), { replace: true });
      })
      .catch(() => {
        if (!cancelled) setStatus('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [token, setSession, navigate]);

  async function handleResend(event: FormEvent) {
    event.preventDefault();
    setResending(true);
    try {
      await authApi.resendVerification(resendEmail);
      setResent(true);
    } finally {
      setResending(false);
    }
  }

  return (
    <AuthShell title={t('auth.verify.title')}>
      {status === 'pending' && (
        <p className="text-sm" style={{ color: '#94A3B8' }}>
          {t('auth.verify.pending')}
        </p>
      )}

      {status === 'success' && <AuthSuccess message={t('auth.verify.success')} />}

      {status === 'failed' && (
        <>
          <AuthError message={t('auth.verify.failed')} />
          {resent ? (
            <AuthSuccess message={t('auth.verify.resent')} />
          ) : (
            <form onSubmit={handleResend} className="space-y-4">
              <label className="block text-sm">
                <span style={{ color: '#94A3B8' }}>{t('auth.verify.resendEmailLabel')}</span>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  className="login-field mt-1 w-full px-3 py-2 outline-none"
                />
              </label>
              <button
                type="submit"
                disabled={resending}
                className="login-submit w-full px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
              >
                {t('auth.verify.resend')}
              </button>
            </form>
          )}
          <p className="text-center text-sm" style={{ color: '#64748B' }}>
            <Link to="/login" style={{ color: '#A78BFA' }} className="underline">
              {t('auth.forgot.backToLogin')}
            </Link>
          </p>
        </>
      )}
    </AuthShell>
  );
}
